import { Controller } from "@hotwired/stimulus"

// Contrôleur pour gérer les actes multiples dans les prestations
export default class extends Controller {
  static targets = ["actesContainer", "acteTemplate", "addButton", "totalGeneral", "acteCount"]
  static values = {
    maxActes: { type: Number, default: 10 }
  }

  connect() {
    this.acteIndex = 0
    this.updateActeCount()
    this.updateTotalGeneral()

    // Vérifier si on a déjà des actes existants
    const existingActes = this.actesContainerTarget.querySelectorAll('.acte-item')
    this.acteIndex = existingActes.length

    this.checkMaxActes()

    // Auto-ajouter le premier acte si le conteneur est vide (nouveau formulaire)
    if (existingActes.length === 0 && this.hasActeTemplateTarget) {
      this.autoAddFirstActe()
    }

    // Vérifier les règles de remboursement pour les actes existants (en mode édition)
    this.checkExistingActesReimbursementRules()
  }

  // Ajouter automatiquement le premier acte à l'ouverture du formulaire
  autoAddFirstActe() {
    if (this.getCurrentActeCount() >= this.maxActesValue) return

    const template = this.acteTemplateTarget.content.cloneNode(true)
    const acteItem = template.querySelector('.acte-item')

    // Remplacer les index dans les noms de champs
    const html = acteItem.innerHTML.replace(/NEW_RECORD/g, this.acteIndex)
    acteItem.innerHTML = html

    // Mettre à jour le numéro d'acte
    const acteNumber = acteItem.querySelector('.acte-number')
    if (acteNumber) acteNumber.textContent = '1'

    // Ajouter au conteneur
    this.actesContainerTarget.appendChild(acteItem)

    this.acteIndex++
    this.updateActeCount()
    this.checkMaxActes()
  }

  // Ajouter un nouvel acte
  addActe(event) {
    event.preventDefault()

    if (this.getCurrentActeCount() >= this.maxActesValue) {
      alert(`Vous ne pouvez pas ajouter plus de ${this.maxActesValue} actes`)
      return
    }

    // Cloner le template
    const template = this.acteTemplateTarget.content.cloneNode(true)
    const acteItem = template.querySelector('.acte-item')

    // Remplacer les index dans les noms de champs
    const html = acteItem.innerHTML.replace(/NEW_RECORD/g, this.acteIndex)
    acteItem.innerHTML = html

    // Ajouter au conteneur
    this.actesContainerTarget.appendChild(acteItem)

    this.acteIndex++
    this.updateActeCount()
    this.checkMaxActes()
  }

  // Supprimer un acte
  removeActe(event) {
    event.preventDefault()

    const acteItem = event.target.closest('.acte-item')

    // Si l'acte a un champ _destroy, le marquer comme supprimé
    const destroyField = acteItem.querySelector('input[name*="[_destroy]"]')
    if (destroyField) {
      destroyField.value = '1'
      acteItem.style.display = 'none'
      // Désactiver les champs required pour éviter que la validation HTML5
      // bloque le formulaire sur des champs cachés
      acteItem.querySelectorAll('[required]').forEach(field => field.removeAttribute('required'))
      // Supprimer les alertes de remboursement de l'acte caché
      acteItem.querySelectorAll('.alert-danger.reimbursement-alert').forEach(alert => alert.remove())
    } else {
      // Sinon, supprimer directement du DOM
      acteItem.remove()
    }

    this.updateActeCount()
    this.updateTotalGeneral()
    this.checkMaxActes()
    // Réévaluer le bouton submit après suppression d'un acte
    this.enableSubmitButton()
  }

  // Calculer le prix total pour un acte (quantité * prix unitaire)
  calculateActeTotal(event) {
    const acteItem = event.target.closest('.acte-item')
    const quantiteInput = acteItem.querySelector('input[name*="[quantite]"]')
    const prixUnitaireInput = acteItem.querySelector('input[name*="[prix_unitaire]"]')
    const prixTotalDisplay = acteItem.querySelector('.prix-total-acte')

    if (quantiteInput && prixUnitaireInput && prixTotalDisplay) {
      const quantite = parseFloat(quantiteInput.value) || 0
      const prixUnitaire = parseFloat(prixUnitaireInput.value) || 0
      const prixTotal = quantite * prixUnitaire

      prixTotalDisplay.textContent = this.formatCurrency(prixTotal)
    }

    this.updateTotalGeneral()
  }

  // Charger les informations d'un type de soin depuis l'API
  async loadTypeSoinInfo(event) {
    const acteItem = event.target.closest('.acte-item')
    const typeSoinId = event.target.value

    if (!typeSoinId) {
      // Masquer tous les champs spécialisés si aucune sélection
      this.hideAllSpecializedFields(acteItem)
      this.removeReimbursementAlert(acteItem)
      return
    }

    try {
      const response = await fetch(`/professionnel_sante/type_soin_mappings/${typeSoinId}.json`)

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`)
      }

      const typeSoin = await response.json()

      // Vérifier que typeSoin n'est pas null
      if (!typeSoin) {
        console.warn('Type de soin non trouvé')
        return
      }

      // Pré-remplir les champs
      const acteCodeInput = acteItem.querySelector('input[name*="[acte_code]"]')
      const nomActeInput = acteItem.querySelector('input[name*="[nom_acte]"]')
      const posteSoinsInput = acteItem.querySelector('input[name*="[poste_soins]"]')

      if (acteCodeInput) acteCodeInput.value = typeSoin.code || ''
      if (nomActeInput) nomActeInput.value = typeSoin.nom || ''
      if (posteSoinsInput) posteSoinsInput.value = typeSoin.poste_soins || ''

      // Afficher les champs spécialisés selon le poste de soins
      this.showSpecializedFields(acteItem, typeSoin.poste_soins)

      // Vérifier la règle de remboursement si on a un assuré
      await this.checkReimbursementRule(acteItem, typeSoinId)

    } catch (error) {
      console.error('Erreur lors du chargement du type de soin:', error)
    }
  }

  // Vérifier la règle de remboursement pour cet acte
  async checkReimbursementRule(acteItem, typeSoinId) {
    // Récupérer l'ID de l'assuré depuis l'URL ou un champ caché
    const assureId = this.getAssureId()

    if (!assureId) {
      // Pas d'assuré sélectionné, on ne peut pas vérifier
      return
    }

    try {
      const response = await fetch(
        `/professionnel_sante/type_soin_mappings/${typeSoinId}/check_reimbursement_rule?assure_id=${assureId}`
      )

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`)
      }

      const result = await response.json()

      // Afficher l'alerte selon le résultat
      this.displayReimbursementAlert(acteItem, result)

    } catch (error) {
      console.error('Erreur lors de la vérification de la règle de remboursement:', error)
    }
  }

  // Récupérer l'ID de l'assuré
  getAssureId() {
    // Méthode 1: depuis un attribut data sur le formulaire (le plus fiable)
    const form = this.element.closest('form')
    if (form && form.dataset.assureId) {
      console.log('Assuré ID trouvé depuis form.dataset.assureId:', form.dataset.assureId)
      return form.dataset.assureId
    }

    // Méthode 2: depuis l'URL (?assure_id=X)
    const urlParams = new URLSearchParams(window.location.search)
    const assureIdFromUrl = urlParams.get('assure_id')
    if (assureIdFromUrl) {
      console.log('Assuré ID trouvé depuis URL:', assureIdFromUrl)
      return assureIdFromUrl
    }

    // Méthode 3: depuis un champ caché dans le formulaire
    const assureIdInput = document.querySelector('input[name="prestation[id_patient]"]')
    if (assureIdInput && assureIdInput.value) {
      // Essayer dataset.assureId d'abord
      if (assureIdInput.dataset.assureId) {
        console.log('Assuré ID trouvé depuis input.dataset.assureId:', assureIdInput.dataset.assureId)
        return assureIdInput.dataset.assureId
      }
    }

    console.warn('Assuré ID non trouvé - impossible de vérifier les règles de remboursement')
    return null
  }

  // Afficher une alerte de règle de remboursement
  displayReimbursementAlert(acteItem, result) {
    // Retirer toute alerte existante
    this.removeReimbursementAlert(acteItem)

    // Créer une nouvelle alerte
    const alertDiv = document.createElement('div')
    alertDiv.className = `alert alert-${result.alert_type} reimbursement-alert mt-3`
    alertDiv.setAttribute('role', 'alert')

    let alertContent = `<strong>${result.message}</strong>`

    if (result.info_messages && result.info_messages.length > 0) {
      alertContent += '<ul class="mb-0 mt-2">'
      result.info_messages.forEach(msg => {
        alertContent += `<li>${msg}</li>`
      })
      alertContent += '</ul>'
    }

    alertDiv.innerHTML = alertContent

    // Insérer l'alerte après le champ de sélection du type de soin
    const typeSoinGroup = acteItem.querySelector('select[name*="[type_soin_mapping_id]"]').closest('.form-group')
    typeSoinGroup.parentNode.insertBefore(alertDiv, typeSoinGroup.nextSibling)

    // Si erreur bloquante, désactiver le bouton de soumission et vider la sélection
    if (!result.success) {
      this.disableSubmitButton()

      // Optionnel: vider la sélection du type de soin pour forcer un nouveau choix
      if (result.alert_type === 'danger') {
        const selectElement = acteItem.querySelector('select[name*="[type_soin_mapping_id]"]')
        if (selectElement) {
          selectElement.value = ''
          // Vider aussi les champs pré-remplis
          const acteCodeInput = acteItem.querySelector('input[name*="[acte_code]"]')
          const nomActeInput = acteItem.querySelector('input[name*="[nom_acte]"]')
          const posteSoinsInput = acteItem.querySelector('input[name*="[poste_soins]"]')
          if (acteCodeInput) acteCodeInput.value = ''
          if (nomActeInput) nomActeInput.value = ''
          if (posteSoinsInput) posteSoinsInput.value = ''
        }
      }
    } else {
      this.enableSubmitButton()
    }
  }

  // Retirer l'alerte de règle de remboursement
  removeReimbursementAlert(acteItem) {
    const existingAlert = acteItem.querySelector('.reimbursement-alert')
    if (existingAlert) {
      existingAlert.remove()
    }
  }

  // Désactiver le bouton de soumission
  disableSubmitButton() {
    const submitButton = this.element.closest('form').querySelector('input[type="submit"], button[type="submit"]')
    if (submitButton) {
      submitButton.disabled = true
      submitButton.classList.add('disabled')
      submitButton.title = 'Veuillez corriger les erreurs de règles de remboursement'
    }
  }

  // Réactiver le bouton de soumission
  enableSubmitButton() {
    const submitButton = this.element.closest('form').querySelector('input[type="submit"], button[type="submit"]')
    if (submitButton) {
      // Ne vérifier que les alertes dans les actes VISIBLES (pas les actes marqués _destroy)
      const visibleActes = this.element.querySelectorAll('.acte-item:not([style*="display: none"])')
      let hasErrors = false
      visibleActes.forEach(acte => {
        if (acte.querySelector('.alert-danger.reimbursement-alert')) {
          hasErrors = true
        }
      })
      if (!hasErrors) {
        submitButton.disabled = false
        submitButton.classList.remove('disabled')
        submitButton.title = ''
      }
    }
  }

  // Afficher les champs spécialisés selon le poste de soins
  showSpecializedFields(acteItem, posteSoins) {
    // D'abord masquer tous les champs spécialisés
    this.hideAllSpecializedFields(acteItem)

    // Puis afficher uniquement les champs pertinents
    const fieldsToShow = {
      'Optique': ['optique-fields'],
      'Dentaire': ['dentaire-fields'],
      'Imagerie médicale': ['imagerie-fields'],
      'Radiologie': ['imagerie-fields'],
      'Laboratoire': ['laboratoire-fields'],
      'Cardiologie': ['cardiologie-fields']
    }

    const fields = fieldsToShow[posteSoins] || []
    fields.forEach(fieldClass => {
      const fieldGroup = acteItem.querySelector(`.${fieldClass}`)
      if (fieldGroup) {
        fieldGroup.classList.remove('d-none')
      }
    })
  }

  // Masquer tous les champs spécialisés
  hideAllSpecializedFields(acteItem) {
    const allFieldGroups = [
      'optique-fields',
      'dentaire-fields',
      'imagerie-fields',
      'laboratoire-fields',
      'cardiologie-fields'
    ]

    allFieldGroups.forEach(fieldClass => {
      const fieldGroup = acteItem.querySelector(`.${fieldClass}`)
      if (fieldGroup) {
        fieldGroup.classList.add('d-none')
      }
    })
  }

  // Mettre à jour le total général
  updateTotalGeneral() {
    if (!this.hasTotalGeneralTarget) return

    let total = 0
    const acteItems = this.actesContainerTarget.querySelectorAll('.acte-item:not([style*="display: none"])')

    acteItems.forEach(acteItem => {
      const quantiteInput = acteItem.querySelector('input[name*="[quantite]"]')
      const prixUnitaireInput = acteItem.querySelector('input[name*="[prix_unitaire]"]')

      if (quantiteInput && prixUnitaireInput) {
        const quantite = parseFloat(quantiteInput.value) || 0
        const prixUnitaire = parseFloat(prixUnitaireInput.value) || 0
        total += quantite * prixUnitaire
      }
    })

    this.totalGeneralTarget.textContent = this.formatCurrency(total)
  }

  // Mettre à jour le compteur d'actes
  updateActeCount() {
    if (!this.hasActeCountTarget) return

    const count = this.getCurrentActeCount()
    this.acteCountTarget.textContent = `${count} / ${this.maxActesValue}`

    // Changer la couleur si on approche de la limite
    if (count >= this.maxActesValue) {
      this.acteCountTarget.classList.add('text-danger')
      this.acteCountTarget.classList.remove('text-warning', 'text-success')
    } else if (count >= this.maxActesValue * 0.8) {
      this.acteCountTarget.classList.add('text-warning')
      this.acteCountTarget.classList.remove('text-danger', 'text-success')
    } else {
      this.acteCountTarget.classList.add('text-success')
      this.acteCountTarget.classList.remove('text-danger', 'text-warning')
    }
  }

  // Obtenir le nombre actuel d'actes (non supprimés)
  getCurrentActeCount() {
    const acteItems = this.actesContainerTarget.querySelectorAll('.acte-item')
    let count = 0

    acteItems.forEach(item => {
      const destroyField = item.querySelector('input[name*="[_destroy]"]')
      if (!destroyField || destroyField.value !== '1') {
        if (item.style.display !== 'none') {
          count++
        }
      }
    })

    return count
  }

  // Vérifier et désactiver le bouton d'ajout si limite atteinte
  checkMaxActes() {
    if (!this.hasAddButtonTarget) return

    const count = this.getCurrentActeCount()

    if (count >= this.maxActesValue) {
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add('disabled')
      this.addButtonTarget.title = `Limite de ${this.maxActesValue} actes atteinte`
    } else {
      this.addButtonTarget.disabled = false
      this.addButtonTarget.classList.remove('disabled')
      this.addButtonTarget.title = 'Ajouter un acte'
    }
  }

  // Formater un montant en devise
  formatCurrency(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount) + ' FCFA'
  }

  // Vérifier les règles de remboursement pour les actes existants (mode édition)
  checkExistingActesReimbursementRules() {
    const existingActes = this.actesContainerTarget.querySelectorAll('.acte-item')

    existingActes.forEach(acteItem => {
      const typeSoinSelect = acteItem.querySelector('select[name*="[type_soin_mapping_id]"]')

      if (typeSoinSelect && typeSoinSelect.value) {
        console.log('Vérification de la règle pour acte existant, type_soin_id:', typeSoinSelect.value)
        // Vérifier la règle de remboursement pour cet acte
        this.checkReimbursementRule(acteItem, typeSoinSelect.value)

        // Afficher les champs spécialisés si nécessaire
        const posteSoinsInput = acteItem.querySelector('input[name*="[poste_soins]"]')
        if (posteSoinsInput && posteSoinsInput.value) {
          console.log('Affichage des champs spécialisés pour poste:', posteSoinsInput.value)
          this.showSpecializedFields(acteItem, posteSoinsInput.value)
        }
      }
    })
  }
}
